import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { uploadImage } from "@/lib/uploads";
import { useT } from "@/i18n";
import { SECTION_KINDS } from "@/components/ambassador-content";

type Section = {
  id: string;
  kind: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  image_url: string | null;
  link_url: string | null;
  year: number | null;
  sort_order: number;
  is_public: boolean;
};

type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string;
  cover_url: string | null;
  link_url: string | null;
  is_published: boolean;
  published_at: string;
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

/**
 * Ambassador page editor. Visible to Official Platform Ambassadors for their
 * own page, and to admins (who can pick any ambassador page to edit).
 */
export function AmbassadorPanel({
  userId,
  isAmbassador,
  isAdmin,
}: {
  userId: string | undefined;
  isAmbassador: boolean;
  isAdmin: boolean;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<string>("");

  const { data: ambassadors } = useQuery({
    queryKey: ["ambassadors"],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,display_name,slug")
        .eq("is_ambassador", true)
        .order("display_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const ownerId = (isAdmin && target) || (isAmbassador ? userId : undefined);

  const { data: sections } = useQuery({
    queryKey: ["profile-sections", ownerId],
    enabled: Boolean(ownerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_sections")
        .select("*")
        .eq("user_id", ownerId!)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as Section[];
    },
  });

  const { data: articles } = useQuery({
    queryKey: ["profile-articles", ownerId],
    enabled: Boolean(ownerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_articles")
        .select("*")
        .eq("user_id", ownerId!)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Article[];
    },
  });

  // Section draft
  const [kind, setKind] = useState<string>("about");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [year, setYear] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [image, setImage] = useState("");

  // Article draft
  const [aTitle, setATitle] = useState("");
  const [aExcerpt, setAExcerpt] = useState("");
  const [aBody, setABody] = useState("");
  const [aLink, setALink] = useState("");
  const [aCover, setACover] = useState("");

  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Page SEO (title, description and a dedicated social preview image).
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDesc, setSeoDesc] = useState("");
  const [ogImage, setOgImage] = useState("");
  const [seoBusy, setSeoBusy] = useState(false);

  const { data: seo } = useQuery({
    queryKey: ["profile-seo", ownerId],
    enabled: Boolean(ownerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("seo_title,seo_description,og_image_url")
        .eq("id", ownerId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    setSeoTitle(seo?.seo_title ?? "");
    setSeoDesc(seo?.seo_description ?? "");
    setOgImage(seo?.og_image_url ?? "");
  }, [seo]);

  if (!isAmbassador && !isAdmin) return null;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["profile-sections", ownerId] });
    await queryClient.invalidateQueries({ queryKey: ["profile-articles", ownerId] });
  }

  async function upload(file: File | undefined, setter: (url: string) => void) {
    if (!file || !ownerId) return;
    setUploading(true);
    try {
      setter(await uploadImage(file, ownerId, "page"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profiles.ambError"));
    } finally {
      setUploading(false);
    }
  }

  async function saveSeo(event: React.FormEvent) {
    event.preventDefault();
    if (!ownerId) return;
    setSeoBusy(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        seo_title: seoTitle.trim() || null,
        seo_description: seoDesc.trim() || null,
        og_image_url: ogImage.trim() || null,
      })
      .eq("id", ownerId);
    setSeoBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["profile-seo", ownerId] });
    toast.success(t("profiles.ambSeoSaved"));
  }

  async function addSection(event: React.FormEvent) {
    event.preventDefault();
    if (!ownerId || !title.trim()) {
      toast.error(t("profiles.ambNeedTitle"));
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.from("profile_sections").insert({
        user_id: ownerId,
        kind,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        body: body.trim() || null,
        link_url: link.trim() || null,
        image_url: image || null,
        year: year ? Number(year) : null,
        sort_order: (sections?.length ?? 0) + 1,
      });
      if (error) throw error;
      setTitle("");
      setSubtitle("");
      setYear("");
      setBody("");
      setLink("");
      setImage("");
      await refresh();
      toast.success(t("profiles.ambSaved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profiles.ambError"));
    } finally {
      setBusy(false);
    }
  }

  async function addArticle(event: React.FormEvent) {
    event.preventDefault();
    if (!ownerId || !aTitle.trim()) {
      toast.error(t("profiles.ambNeedTitle"));
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.from("profile_articles").insert({
        user_id: ownerId,
        slug: `${slugify(aTitle)}-${Date.now().toString(36)}`,
        title: aTitle.trim(),
        excerpt: aExcerpt.trim() || null,
        body: aBody.trim(),
        cover_url: aCover || null,
        link_url: aLink.trim() || null,
      });
      if (error) throw error;
      setATitle("");
      setAExcerpt("");
      setABody("");
      setALink("");
      setACover("");
      await refresh();
      toast.success(t("profiles.ambSaved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profiles.ambError"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(table: "profile_sections" | "profile_articles", id: string) {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await refresh();
    toast.success(t("profiles.ambDeleted"));
  }

  return (
    <section className="card-surface space-y-6 p-6">
      <div>
        <h2 className="font-display text-xl font-bold">{t("profiles.ambPanelTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("profiles.ambPanelHint")}</p>
      </div>

      {isAdmin && (
        <div className="space-y-2">
          <Label htmlFor="amb-target">{t("profiles.ambEditingFor")}</Label>
          <select
            id="amb-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
          >
            <option value="">
              {isAmbassador ? t("profiles.ambMyPage") : t("profiles.ambSelectPage")}
            </option>
            {(ambassadors ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name ?? a.slug} (/user/{a.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {!ownerId ? (
        <p className="text-sm text-muted-foreground">{t("profiles.ambSelectPage")}</p>
      ) : (
        <>
          {/* Page SEO */}
          <form onSubmit={saveSeo} className="space-y-4 rounded-xl border border-border p-4">
            <div>
              <h3 className="font-display font-semibold">{t("profiles.ambSeoTitle")}</h3>
              <p className="text-sm text-muted-foreground">{t("profiles.ambSeoHint")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amb-seo-title">{t("profiles.ambSeoMetaTitle")}</Label>
              <Input
                id="amb-seo-title"
                value={seoTitle}
                maxLength={70}
                placeholder={t("profiles.ambSeoTitlePlaceholder")}
                onChange={(e) => setSeoTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amb-seo-desc">{t("profiles.ambSeoMetaDesc")}</Label>
              <Textarea
                id="amb-seo-desc"
                rows={3}
                maxLength={200}
                value={seoDesc}
                placeholder={t("profiles.ambSeoDescPlaceholder")}
                onChange={(e) => setSeoDesc(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("profiles.ambSeoImage")}</Label>
              <p className="text-xs text-muted-foreground">{t("profiles.ambSeoImageHint")}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Label
                  htmlFor="amb-og"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
                >
                  <Upload className="size-4" />
                  {uploading ? t("profiles.ambUploading") : t("profiles.ambSeoUpload")}
                </Label>
                <input
                  id="amb-og"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void upload(e.target.files?.[0], setOgImage)}
                />
                {ogImage && (
                  <Button type="button" variant="ghost" onClick={() => setOgImage("")}>
                    <Trash2 className="size-4" /> {t("profiles.ambSeoRemoveImage")}
                  </Button>
                )}
              </div>
              {ogImage && (
                <img
                  src={ogImage}
                  alt={t("profiles.ambSeoImage")}
                  className="mt-2 aspect-[1200/630] w-full max-w-md rounded-xl border border-border object-cover"
                />
              )}
            </div>
            <Button type="submit" variant="hot" disabled={seoBusy || uploading}>
              {seoBusy ? t("profiles.ambSaving") : t("profiles.ambSeoSave")}
            </Button>
          </form>

          {/* Sections */}
          <form onSubmit={addSection} className="space-y-4 rounded-xl border border-border p-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="amb-kind">{t("profiles.ambKind")}</Label>
                <select
                  id="amb-kind"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
                >
                  {SECTION_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {t(k.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="amb-title">{t("profiles.ambTitle")}</Label>
                <Input id="amb-title" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="amb-sub">{t("profiles.ambSubtitle")}</Label>
                <Input id="amb-sub" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amb-year">{t("profiles.ambYear")}</Label>
                <Input
                  id="amb-year"
                  inputMode="numeric"
                  value={year}
                  onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amb-body">{t("profiles.ambBody")}</Label>
              <Textarea id="amb-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="amb-link">{t("profiles.ambLink")}</Label>
                <Input
                  id="amb-link"
                  value={link}
                  placeholder="https://"
                  onChange={(e) => setLink(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amb-image" className="cursor-pointer">
                  {t("profiles.ambImage")}
                </Label>
                <div className="flex items-center gap-3">
                  <Label
                    htmlFor="amb-image"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Upload className="size-4" />
                    {uploading ? t("profiles.ambUploading") : t("profiles.ambImage")}
                  </Label>
                  <input
                    id="amb-image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void upload(e.target.files?.[0], setImage)}
                  />
                  {image && <img src={image} alt="" className="size-10 rounded object-cover" />}
                </div>
              </div>
            </div>
            <Button type="submit" variant="hot" disabled={busy}>
              <Plus className="size-4" /> {t("profiles.ambAddSection")}
            </Button>
          </form>

          <div className="space-y-2">
            {(sections ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">{t("profiles.ambNone")}</p>
            )}
            {(sections ?? []).map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {s.year ? `${s.year} · ` : ""}
                    {s.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(SECTION_KINDS.find((k) => k.value === s.kind)?.labelKey ?? "profiles.ambKind")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => void remove("profile_sections", s.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Articles */}
          <form onSubmit={addArticle} className="space-y-4 rounded-xl border border-border p-4">
            <h3 className="font-display font-semibold">{t("profiles.ambArticles")}</h3>
            <div className="space-y-2">
              <Label htmlFor="art-title">{t("profiles.ambArticleTitle")}</Label>
              <Input id="art-title" value={aTitle} onChange={(e) => setATitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="art-excerpt">{t("profiles.ambExcerpt")}</Label>
              <Input id="art-excerpt" value={aExcerpt} onChange={(e) => setAExcerpt(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="art-body">{t("profiles.ambBody")}</Label>
              <Textarea id="art-body" rows={6} value={aBody} onChange={(e) => setABody(e.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="art-link">{t("profiles.ambLink")}</Label>
                <Input
                  id="art-link"
                  value={aLink}
                  placeholder="https://"
                  onChange={(e) => setALink(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="art-cover" className="cursor-pointer">
                  {t("profiles.ambCover")}
                </Label>
                <div className="flex items-center gap-3">
                  <Label
                    htmlFor="art-cover"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Upload className="size-4" />
                    {uploading ? t("profiles.ambUploading") : t("profiles.ambCover")}
                  </Label>
                  <input
                    id="art-cover"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void upload(e.target.files?.[0], setACover)}
                  />
                  {aCover && <img src={aCover} alt="" className="size-10 rounded object-cover" />}
                </div>
              </div>
            </div>
            <Button type="submit" variant="hot" disabled={busy}>
              <Plus className="size-4" /> {t("profiles.ambPublishArticle")}
            </Button>
          </form>

          <div className="space-y-2">
            {(articles ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">{t("profiles.ambNone")}</p>
            )}
            {(articles ?? []).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{a.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(a.published_at).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => void remove("profile_articles", a.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
